import type { WorldSymbol, WorldTheme } from "./worldTypes";

/**
 * PR31 follow-up (visual-language pass, world-specific obstacle identity).
 * Obstacles keep the exact same shape/collision/geometry in every world -
 * only their fill tint and border accent vary, so they always read as the
 * same "obstacle" family (never five different designs). The mapping is
 * centralized here (a single lookup, not five call sites each picking a
 * color) so `GameScene.renderObstacles()` stays a pure consumer.
 *
 * The pattern for four of the five worlds is uniform: `fill` is the
 * world's own `secondary` tint (already the darker/base tone used for that
 * world's own gradient band elsewhere), `border` is the world's own
 * `accent` (already that world's brightest, most recognizable identity
 * color - the same color used for its sphere icon on World Select). That
 * keeps the obstacle's world tint visually consistent with how players
 * already recognize each world everywhere else on screen.
 *
 * SATURN is the one deliberate exception: its `accent`/`contour` are both
 * gold (the ring color), which would read as the same warm family as
 * Mars/Sun and contradict Saturn's own "violet/blue orbital" identity.
 * Saturn's `primary`/`secondary` are already genuine navy-blue tones (see
 * `WorldRegistry`'s own "deep navy/restrained orbital feel" comment on
 * that theme), so Saturn's border is a lightened version of its own
 * `secondary` instead - still derived entirely from Saturn's own existing
 * palette, never an invented hue.
 *
 * PR31 follow-up fix (POLISH 2): SUN is now a second deliberate exception -
 * `glow`/`hotContour` give its obstacles a restrained "hot/burning"
 * identity (world-identity polish, not a mechanic - collision geometry,
 * fill, and outer border are all completely unchanged). Every other world
 * leaves both `undefined`, so `renderObstacles()`'s extra draw calls for
 * them are simply skipped - this stays purely additive for Sun.
 */
export interface ObstacleWorldStyle {
  /** Obstacle fill color for this world. */
  readonly fill: number;
  /** Obstacle border/bevel color for this world - used for both the outer stroke and the existing faint inner bevel stroke, UNLESS `hotContour` overrides the latter. */
  readonly border: number;
  /** SUN only: a soft, low-alpha ember-colored halo drawn just outside the obstacle's own border - "these obstacles radiate heat", never a literal flame/particle. `undefined` for every other world (no glow drawn). */
  readonly glow?: number;
  /** SUN only: replaces the existing faint same-tone inner bevel stroke with a brighter, hotter tone (a lightened `accent`) - reads as a glowing inner edge/core rather than a flat depth cue. `undefined` for every other world (the existing same-tone bevel is used, unchanged). */
  readonly hotContour?: number;
}

/** Blends `hex` toward white by `amount` (0 = unchanged, 1 = white). Pure integer RGB math - no Phaser dependency, so this (and everything else in this module) stays directly unit-testable. */
function lighten(hex: number, amount: number): number {
  const r = (hex >> 16) & 0xff;
  const g = (hex >> 8) & 0xff;
  const b = hex & 0xff;
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  return (lr << 16) | (lg << 8) | lb;
}

const STYLE_BY_SYMBOL: Record<WorldSymbol, (theme: WorldTheme) => ObstacleWorldStyle> = {
  moon: (theme) => ({ fill: theme.secondary, border: theme.accent }),
  earth: (theme) => ({ fill: theme.secondary, border: theme.accent }),
  mars: (theme) => ({ fill: theme.secondary, border: theme.accent }),
  // POLISH 2: `glow` reuses Sun's own existing bright-orange `accent`
  // (already this world's "heat" identity color everywhere else on
  // screen) for the outer halo; `hotContour` is a further-lightened
  // version of that SAME color for the inner edge, so the whole effect
  // stays a single, coherent warm-to-hot gradient - never an invented
  // second hue competing with Sun's own established palette.
  sun: (theme) => ({ fill: theme.secondary, border: theme.accent, glow: theme.accent, hotContour: lighten(theme.accent, 0.4) }),
  saturn: (theme) => ({ fill: theme.primary, border: lighten(theme.secondary, 0.35) }),
};

/**
 * This world's obstacle fill/border pair. `STYLE_BY_SYMBOL` is typed as
 * `Record<WorldSymbol, ...>`, so a future `WorldSymbol` with no entry there
 * fails to compile at that declaration - this lookup itself never needs
 * its own runtime exhaustiveness check.
 */
export function obstacleStyleForWorld(theme: WorldTheme): ObstacleWorldStyle {
  return STYLE_BY_SYMBOL[theme.symbol](theme);
}
